import React from 'react'
import { Tabs, Avatar, Table, Typography } from 'antd'
import { Fade, Zoom } from 'react-awesome-reveal'
import PropTypes from 'prop-types'

import { translate } from '../../../cls/TranslationContext'
import './ServicesOverview.css'
import { isMobile } from 'react-device-detect'

const { Title, Paragraph } = Typography

const tabs = [
	{
		key: 'wordpress',
		title: translate('home.servicesWp'),
		avatar: '/img/wordpress-icon.png',
		description: translate('home.servicesWpDesc'),
		columns: [
			{
				title: translate('single.feature.label'),
				dataIndex: 'feature',
				key: 'feature'
			},
			{
				title: translate('home.servicesWpColBusiness'),
				dataIndex: 'value',
				key: 'value'
			},
			{
				title: translate('single.time-impact'),
				dataIndex: 'impact',
				key: 'impact'
			}
		],
		data: [
			{
				key: 1,
				feature: translate('home.servicesWpGutenberg'),
				value: translate('home.servicesWpValue'),
				impact: translate('home.servicesWpSaved')
			},
			{
				key: 2,
				feature: translate('home.servicesWpAi'),
				value: translate('home.servicesWpDynamicAi'),
				impact: translate('home.servicesWpCut')
			},
			{
				key: 3,
				feature: translate('home.servicesWpCustom'),
				value: translate('home.servicesWpSeo'),
				impact: `+30 ${translate('home.servicesWpLighthouse')}`
			},
			{
				key: 4,
				feature: translate('home.servicesWpMonitor'),
				value: translate('home.servicesWpData'),
				impact: translate('home.servicesWpProtection')
			}
		]
	},
	{
		key: 'programming',
		title: translate('services.programming.label'),
		avatar: '/img/programming-icon.png',
		description: translate('services.programming.desc'),
		columns: [
			{
				title: translate('services.programming.deliver'),
				dataIndex: 'feature',
				key: 'feature'
			},
			{
				title: translate('services.programming.use-case'),
				dataIndex: 'value',
				key: 'value'
			},
			{
				title: translate('single.time-impact'),
				dataIndex: 'impact',
				key: 'impact'
			}
		],
		data: [
			{
				key: 1,
				feature: translate('services.programming.react'),
				value: translate('services.programming.tools'),
				impact: translate('services.programming.dev')
			},
			{
				key: 2,
				feature: translate('services.programming.python'),
				value: translate('services.programming.scrape'),
				impact: translate('services.programming.save')
			},
			{
				key: 3,
				feature: translate('services.programming.ci'),
				value: translate('services.programming.process'),
				impact: translate('services.programming.peace')
			},
			{
				key: 4,
				feature: translate('services.programming.secure'),
				value: translate('services.programming.grade'),
				impact: translate('services.programming.sync')
			}
		]
	},
	{
		key: 'ai',
		title: translate('services.ai.label'),
		avatar: '/img/ai-icon.png',
		description: translate('services.ai.desc'),
		columns: [
			{
				title: translate('services.ai.feature'),
				dataIndex: 'feature',
				key: 'feature'
			},
			{
				title: translate('services.ai.use-case'),
				dataIndex: 'value',
				key: 'value'
			},
			{
				title: translate('services.ai.gain'),
				dataIndex: 'impact',
				key: 'impact'
			}
		],
		data: [
			{
				key: 1,
				feature: translate('services.ai.gpt'),
				value: translate('services.ai.landing'),
				impact: translate('services.ai.produce')
			},
			{
				key: 2,
				feature: translate('services.ai.search'),
				value: translate('services.ai.help'),
				impact: translate('services.ai.seo')
			},
			{
				key: 3,
				feature: translate('services.ai.scoring'),
				value: translate('services.ai.classify'),
				impact: translate('services.ai.save')
			},
			{
				key: 4,
				feature: translate('services.ai.vision'),
				value: translate('services.ai.automate'),
				impact: translate('services.ai.replace')
			}
		]
	},
	{
		key: 'marketing',
		title: translate('services.marketing.label'),
		avatar: '/img/marketing-icon.png',
		description: translate('services.marketing.desc'),
		columns: [
			{
				title: translate('services.programming.deliver'),
				dataIndex: 'feature',
				key: 'feature'
			},
			{
				title: translate('services.marketing.purpose'),
				dataIndex: 'value',
				key: 'value'
			},
			{
				title: translate('services.marketing.boost'),
				dataIndex: 'impact',
				key: 'impact'
			}
		],
		data: [
			{
				key: 1,
				feature: translate('services.marketing.ai'),
				value: translate('services.marketing.target'),
				impact: `+20–40% ${translate('services.marketing.conversion')}`
			},
			{
				key: 2,
				feature: translate('services.marketing.funnel'),
				value: translate('services.marketing.auto'),
				impact: translate('services.marketing.cycles')
			},
			{
				key: 3,
				feature: translate('services.marketing.content'),
				value: translate('services.marketing.plan'),
				impact: translate('services.marketing.save-time')
			},
			{
				key: 4,
				feature: translate('services.marketing.monitor'),
				value: translate('services.marketing.analysis'),
				impact: translate('services.marketing.rank')
			}
		]
	}
]

const ServicesOverview = ({
	title,
	disableSections = {},
	wrapperStyles = {},
	children = null
}) => {
	const items = tabs.map((tab) => ({
		key: tab.key,
		label: (
			<span>
				<Avatar src={tab.avatar} style={{ marginRight: 8 }} />
				{tab.title}
			</span>
		),
		children: (
			<>
				<Fade triggerOnce direction="up" damping={0.15}>
					<Paragraph
						style={{
							maxWidth: '800px',
							marginBottom: 24,
							fontSize: '16px'
						}}
					>
						{tab.description}
					</Paragraph>
				</Fade>
				<Zoom triggerOnce>
					<Table
						columns={tab.columns}
						dataSource={tab.data}
						pagination={false}
						bordered
						size="middle"
						rowKey="key"
						className="service-table"
					/>
				</Zoom>
			</>
		)
	}))

	return (
		<div className="services-container" style={{ ...wrapperStyles }}>
			{disableSections?.title !== true && (
				<Title
					level={2}
					style={
						isMobile
							? {}
							: { textAlign: 'center', marginBottom: 40 }
					}
				>
					{title ?? translate('services.content.title')}
				</Title>
			)}
			{children && children}

			<Tabs
				defaultActiveKey="wordpress"
				type="line"
				size="large"
				tabPosition="top"
				items={items}
			/>
		</div>
	)
}

ServicesOverview.propTypes = {
	title: PropTypes.string,
	disableSections: PropTypes.object,
	wrapperStyles: PropTypes.object,
	children: PropTypes.node
}
export default ServicesOverview
